import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import AuthLayout, { AuthLink } from './AuthLayout';
import { Button, Input, Checkbox, Alert } from '../../components/ui';

function Login() {
  const { isLoggedIn, logIn } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setFormData({ username: '', password: '', rememberMe: false });
    }
  }, [isLoggedIn]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setErrorMessage('Please fill in all fields.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await api.post('/user/login', {
        username: formData.username,
        password: formData.password,
        rememberMe: formData.rememberMe,
      });
      const token = response.data.accessToken;

      let decodedToken;
      try {
        decodedToken = jwtDecode(token);
      } catch {
        throw new Error('Invalid token received from the server.');
      }

      logIn(token, decodedToken.userId, formData.rememberMe);
      navigate('/');
    } catch (error) {
      console.error('Error during login:', error);

      if (error.response && error.response.data) {
        setErrorMessage(error.response.data.message || 'An error occurred. Please try again later.');
      } else {
        setErrorMessage('Unable to connect to the server. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Enter your credentials to access your reports."
      footer={
        <>
          Don&apos;t have an account? <AuthLink to="/signup">Request access</AuthLink>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {errorMessage && (
          <Alert variant="error">
            {errorMessage}
            {errorMessage.includes('User not found') && (
              <>
                {' '}
                <AuthLink to="/signup">Request access</AuthLink>.
              </>
            )}
          </Alert>
        )}

        <Input
          label="Username"
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          disabled={isSubmitting}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="username"
        />

        <Input
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          disabled={isSubmitting}
          autoComplete="current-password"
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Checkbox
            label="Remember me"
            name="rememberMe"
            checked={formData.rememberMe}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          <AuthLink to="/forgot-password">Forgot password?</AuthLink>
        </div>

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default Login;
